<script setup lang="ts">
import { computed, ref } from 'vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import Alert from '@/components/v2/Alert.vue';
import TextField from '@/components/v2/TextField.vue';
import { CoverageType } from '@/types/PolicyType';
import { SubitemKind, subitemKindLabels } from '@/types/PolicySublimit';
import { SubitemType, SubitemTypeSaveRequest } from '@/types/SubitemType';

const NAME_MAX_LENGTH = 100;

const props = defineProps<{
  kind: SubitemKind;
  subitemType: SubitemType | null;
  coverageTypes: CoverageType[];
  save: (request: SubitemTypeSaveRequest) => Promise<void>;
}>();

const emit = defineEmits<{ closed: [] }>();

const label = `${subitemKindLabels[props.kind]} Type`;
const title = props.subitemType ? `Edit ${label}` : `New ${label}`;
const isEpicSourced = props.subitemType?.sourceKey != null;

const coverageTypeId = ref<number | null>(props.subitemType?.coverageTypeId ?? null);
const name = ref(props.subitemType?.name ?? '');
const errorMessage = ref('');
const saving = ref(false);

const trimmedName = computed(() => name.value.trim());
const nameError = computed(() =>
  trimmedName.value.length > NAME_MAX_LENGTH ? `Name must be ${NAME_MAX_LENGTH} characters or fewer.` : '',
);
const selectedCoverageTypeName = computed(
  () => props.coverageTypes.find((coverageType) => coverageType.coverageTypeId === coverageTypeId.value)?.coverageType,
);
const showReassignWarning = computed(
  () =>
    !!props.subitemType &&
    props.subitemType.usageCount > 0 &&
    coverageTypeId.value !== props.subitemType.coverageTypeId,
);
const reassignWarning = computed(() => {
  const count = props.subitemType?.usageCount ?? 0;
  const records = count === 1 ? '1 policy record keeps' : `${count} policy records keep`;
  return `Existing ${records} this type; it will only be offered on ${selectedCoverageTypeName.value} lines going forward.`;
});

function isSaveDisabled(): boolean {
  return (
    saving.value ||
    coverageTypeId.value === null ||
    !!nameError.value ||
    (!isEpicSourced && trimmedName.value.length === 0)
  );
}

async function submit(): Promise<boolean> {
  if (saving.value) {
    return false;
  }
  saving.value = true;
  errorMessage.value = '';
  try {
    await props.save({ coverageTypeId: coverageTypeId.value as number, name: trimmedName.value || null });
    return true;
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : `Failed to save ${label}.`;
    return false;
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <ConfirmModal
    :title="title"
    :subtitle="subitemType?.derivedName"
    :action="submit"
    :is-confirm-disabled="isSaveDisabled"
    :confirm-text="subitemType ? 'Save' : 'Create'"
    size="md"
    :show="true"
    @closed="emit('closed')">
    <div class="flex flex-col gap-4 p-2">
      <div
        v-if="isEpicSourced"
        class="flex flex-wrap gap-x-8 gap-y-1 text-sm text-gray-600"
        data-testid="subitem-type-source">
        <div>
          <span class="font-medium">Source key:</span>
          {{ subitemType?.sourceKey }}
        </div>
        <div>
          <span class="font-medium">Source name:</span>
          {{ subitemType?.sourceName }}
        </div>
      </div>

      <div>
        <label
          for="subitem-type-coverage-type"
          class="mb-1 block text-sm">
          Coverage Type
        </label>
        <select
          id="subitem-type-coverage-type"
          v-model="coverageTypeId"
          class="select select-bordered w-full"
          data-testid="subitem-type-coverage-type">
          <option
            :value="null"
            disabled>
            Select a coverage type
          </option>
          <option
            v-for="coverageType in coverageTypes"
            :key="coverageType.coverageTypeId"
            :value="coverageType.coverageTypeId">
            {{ coverageType.coverageType }}
          </option>
        </select>
      </div>

      <div>
        <span class="mb-1 block text-sm">Display Name</span>
        <TextField
          v-model="name"
          :placeholder="subitemType?.sourceName ?? 'Name'"
          :error="!!nameError"
          :error-message="nameError"
          data-testid="subitem-type-name" />
        <p
          v-if="isEpicSourced && !nameError"
          class="mt-1 text-sm text-gray-500">
          Leave blank to use the Epic name.
        </p>
      </div>

      <Alert
        v-if="showReassignWarning"
        type="warning"
        data-testid="subitem-type-reassign-warning">
        {{ reassignWarning }}
      </Alert>

      <Alert
        v-if="errorMessage"
        type="error"
        data-testid="subitem-type-error">
        {{ errorMessage }}
      </Alert>
    </div>
  </ConfirmModal>
</template>
